// デフォルト値とファクトリ関数。
// 新規ルーレット作成、初回起動時の AppState 構築で使用する。

import type {
  AppPreferences,
  AppState,
  EffectConfig,
  EffectKey,
  Item,
  ItemSet,
  Roulette,
  RouletteSettings,
  SpinEffectSettings,
  UiState
} from './types';
import { SCHEMA_VERSION } from './types';

export const EFFECT_KEYS: EffectKey[] = [
  'soundConfirm', 'soundExpect', 'soundNgConfirm',
  'miniCharTarget', 'miniCharExpect', 'miniCharNg',
  'cutInTarget', 'cutInExpect', 'cutInNg',
  'flashConfirm', 'wheelGlow', 'textChance',
];

/** 排他グループ: 同じグループ内の effect は最大1つだけ抽選される */
export const EFFECT_EXCLUSIVE_GROUPS: readonly (readonly EffectKey[])[] = [
  ['soundConfirm', 'soundExpect', 'soundNgConfirm'],
  ['miniCharTarget', 'miniCharExpect', 'miniCharNg'],
  ['cutInTarget', 'cutInExpect', 'cutInNg'],
];

/** 確率範囲のグループ別デフォルト (ユーザー要望) */
const PROB_SOUND  = { min: 0.01, max: 0.20 };  // 音系: 1〜20%
const PROB_MINI   = { min: 0.01, max: 0.15 };  // ミニキャラ: 1〜15%
const PROB_CUTIN  = { min: 0.01, max: 0.05 };  // カットイン: 1〜5%
const PROB_OTHER  = { min: 0.01, max: 0.10 };  // その他: 1〜10%

/** タイミング範囲は中心値の ±10% で構成するヘルパー */
const tr = (center: number) => ({
  min: Math.max(0, center - 0.10),
  max: Math.min(1, center + 0.10),
});

/** 共通フラグ: probabilityRandom/timingRandom はデフォルト false (範囲指定優先)。
 *  variant のみランダムをデフォルト (selectedVariant=0)。 */
const COMMON = { probabilityRandom: false, timingRandom: false, selectedVariant: 0, enabled: true };

const DEFAULT_EFFECT_CONFIGS: Record<EffectKey, EffectConfig> = {
  soundConfirm:    { ...COMMON, probabilityRange: PROB_SOUND, timingRange: tr(0.70) },
  soundExpect:     { ...COMMON, probabilityRange: PROB_SOUND, timingRange: tr(0.55) },
  soundNgConfirm:  { ...COMMON, probabilityRange: PROB_SOUND, timingRange: tr(0.55) },
  miniCharTarget:  { ...COMMON, probabilityRange: PROB_MINI,  timingRange: tr(0.40) },
  miniCharExpect:  { ...COMMON, probabilityRange: PROB_MINI,  timingRange: tr(0.35) },
  miniCharNg:      { ...COMMON, probabilityRange: PROB_MINI,  timingRange: tr(0.40) },
  cutInTarget:     { ...COMMON, probabilityRange: PROB_CUTIN, timingRange: tr(0.45) },
  cutInExpect:     { ...COMMON, probabilityRange: PROB_CUTIN, timingRange: tr(0.35) },
  cutInNg:         { ...COMMON, probabilityRange: PROB_CUTIN, timingRange: tr(0.45) },
  flashConfirm:    { ...COMMON, probabilityRange: PROB_OTHER, timingRange: tr(0.85) },
  wheelGlow:       { ...COMMON, probabilityRange: PROB_OTHER, timingRange: tr(0.50) },
  textChance:      { ...COMMON, probabilityRange: PROB_OTHER, timingRange: tr(0.45) },
};

export function defaultEffectSettings(): SpinEffectSettings {
  return {
    enabled: false,
    effects: { ...DEFAULT_EFFECT_CONFIGS },
  };
}
import { uuid } from './uuid';

export const WIN_HISTORY_LIMIT = 100;

export function defaultSettings(): RouletteSettings {
  return {
    spinDurationMs: 5000,
    spinDurationRandomRatio: 0,
    spinPresetProfile: 'z',
    spinPresetRandom: false,
    spinDurationRandom: false,
    spinPhaseOverrides: null,
    spinPhaseRandomize: 0,
    doubleStopWindowMs: 400,
    tripleStopWindowMs: 600,
    spinSound: 'click',
    decideSound: 'fanfare',
    textOrientation: 'radial',
    textSizeMode: 'auto',
    textFixedSize: 14,
    textAlign: 'outer',
    markerPosition: 'right',
    markerStyle: 'overlap',
    markerAngleDeg: undefined,
    layoutMode: 'standard',
    randomEachSpin: false,
    spinDirection: 'cw',
    segmentDirection: 'cw',
    spinEffects: defaultEffectSettings(),
  };
}

export function defaultPreferences(): AppPreferences {
  return {
    themeMode: 'auto',
    volume: 50,
    showItemProbability: true,
    showItemWinCount: true,
    // Windows 版同等: シャッフル時は分割を独立ランダム (均等は維持しない)
    randomMaintainEvenness: false
  };
}

export function defaultUi(): UiState {
  return {
    itemListCollapsed: false,
    shuffleOrder: null
  };
}

export function makeItem(text: string): Item {
  return {
    text,
    enabled: true,
    probMode: 'default',
    probValue: undefined,
    splitCount: 1
  };
}

export function makeSet(name: string, items?: Item[]): ItemSet {
  return {
    id: uuid(),
    name,
    items: items && items.length > 0 ? items : [makeItem('項目 1'), makeItem('項目 2')]
  };
}

export function makeRoulette(name: string): Roulette {
  const initialSet = makeSet('デフォルト');
  return {
    id: uuid(),
    name,
    sets: [initialSet],
    currentSetId: initialSet.id,
    settings: defaultSettings(),
    winHistory: [],
    visible: true
  };
}

export function makeInitialAppState(): AppState {
  const r = makeRoulette('ルーレット 1');
  return {
    schemaVersion: SCHEMA_VERSION,
    roulettes: [r],
    activeRouletteId: r.id,
    ui: defaultUi(),
    preferences: defaultPreferences()
  };
}
